import { useCallback, useEffect, useMemo, useState } from 'react';
import { CareGrid } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { slicerParam, sortParam, type SlicerDef } from '@lib/grid';
import {
  directWriteSentence, optOutDecide, optOutReversals, optOutReview,
  type CampaignMarker, type OptOutReversalRow, type OptOutReversalsResp, type OptOutReviewResp, type OptOutReviewRow,
} from '@lib/care-api';
import { fmtMemberId } from '@lib/member-id';
import { useStaleRead } from '@lib/stale-read';
import { LOCK_FIELDSET_STYLE, StaleNote } from '@components/StaleNote';
import { fmtPhone, fmtTs, CAME_BACK_WORDING, CAMPAIGN_MARKER_WORDING, COHORT_WORDING, OPTOUT_REVIEW_WORDING } from '@lib/care-model';

/**
 * Opt-outs (nav 10) - the STOP review screen William asked for on 14 September 2026: "a new table in
 * Care which has all of the people who have asked to STOP communications for the admin to review
 * each communication and then approve the STOP (to ensure that they are each legitimate)".
 *
 * THE ONE THING THIS SCREEN MUST SAY OUT LOUD, and it is at the top of the page rather than in a
 * tooltip: a STOP is enforced from the moment it arrives, so APPROVING CHANGES NOTHING the member
 * experiences. Without that sentence an admin would reasonably believe the member is still being
 * texted until they click approve, which is the opposite of the truth.
 *
 * ONE ROW PER COMMUNICATION, never per member. A member with a thumbs-up reaction, a real STOP and a
 * later UNSTOP has three things to read.
 */

/** The classes where a human actually changes the outcome. */
const DECIDING = ['UNCLEAR', 'STAMP_ONLY', 'FALSE_STOP'] as const;
/** The classes that are already right and only need confirming. */
const CONFIRMING = ['STOP', 'STOP_CHANNEL'] as const;

const CLASS_PILL: Record<string, string> = {
  STOP: 'pill-danger',
  STOP_CHANNEL: 'pill-danger',
  UNCLEAR: 'pill-primary',
  STAMP_ONLY: 'pill-accent',
  FALSE_STOP: 'pill-success',
};

const CLASS_HELP: Record<string, string> = {
  STOP: 'A definitive stop order by the member.',
  STOP_CHANNEL: 'A stop the member made on one channel. It is a full stop: this plan enforces it everywhere.',
  UNCLEAR: 'Short of definitive. This is where your reading changes the outcome.',
  STAMP_ONLY: "Suppressed by the plan's do-not-solicit flag, which records a date and nothing else.",
  FALSE_STOP: 'Already found to be a false stop. The member asked to come back.',
};

/**
 * OPENS ON ALL (William, row 151: "Open on all - and make the table have sort headers ... and then
 * quick-decision buttons on the side"). The three narrower views stay as chips. 'all' is every
 * reviewable row, pending or decided, so an admin sees the whole list and sorts it.
 */
type Mode = 'all' | 'deciding' | 'confirming' | 'decided' | 'reversals';

/** Seconds as a human gap: "36 s", "2 min", "3 h", "5 d". */
function fmtGap(s: number | null): string {
  if (s === null) return '';
  const a = Math.abs(s);
  if (a < 60) return `${s} s`;
  if (a < 3600) return `${Math.round(s / 60)} min`;
  if (a < 86400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86400)} d`;
}
type SortKey = 'at' | 'member' | 'class' | 'source' | 'decision';
/** The keys the review route sorts by (row 356: up to three of them, Shift-click on a heading). */
const SORT_KEYS: readonly SortKey[] = ['member', 'class', 'at', 'source', 'decision'];

const PAGE = 40;
/** ClariConnect's floor for an admin reason (row 216 contract); enforced only while the platform write is on. */
const MIN_REASON = 10;

/**
 * ROW 356: the review's slicers, answered by the SERVER over every row the other filters keep (the
 * screen holds a page of 40). The class and the campaign mark keep their own chips.
 */
const REVIEW_SLICER_DEFS: SlicerDef<OptOutReviewRow>[] = [
  { key: 'channel', label: 'Channel', value: () => null, blankLabel: 'Not recorded' },
  { key: 'source', label: 'Source', value: () => null, blankLabel: 'Not recorded' },
  {
    key: 'decision', label: 'Decision', value: () => null,
    labelFor: (v) => (v === 'pending' ? 'Still to review' : v === 'unclear' ? "Can't tell (still open)" : v === 'approved' ? 'Approved' : v === 'rejected' ? 'Not a stop' : v),
  },
];

/** Came back is one whole list, so its slicers run in the browser over every row. */
const CAME_BACK_SLICERS: SlicerDef<OptOutReversalRow>[] = [
  { key: 'marker', label: 'Campaign', value: (r) => r.campaignMarker, labelFor: (v) => CAMPAIGN_MARKER_WORDING[v as CampaignMarker]?.label ?? v },
  { key: 'kind', label: 'Kind', value: (r) => r.kind, labelFor: (v) => CAME_BACK_WORDING[v as OptOutReversalRow['kind']] ?? v },
  { key: 'source', label: 'Source', value: (r) => r.source, blankLabel: 'Not recorded' },
];

/** The Came back table's columns (the list has no sort on the server; the browser sorts all of it). */
const CAME_BACK_COLUMNS: GridColumn<OptOutReversalRow>[] = [
  {
    key: 'member', label: 'Member', sortValue: (r) => r.memberName,
    render: (r) => (
      <>
        <div>{r.memberName || <span className="muted">unmatched</span>}</div>
        <div className="micro muted">
          {r.memberId ? fmtMemberId(r.memberId) : ''}{r.phone ? ` - ${fmtPhone(r.phone)}` : ''}
        </div>
      </>
    ),
  },
  {
    key: 'campaign', label: 'Campaign', sortValue: (r) => CAMPAIGN_MARKER_WORDING[r.campaignMarker].label,
    render: (r) => (
      <span className={`pill ${CAMPAIGN_MARKER_WORDING[r.campaignMarker].pill}`} title={CAMPAIGN_MARKER_WORDING[r.campaignMarker].help}>
        {CAMPAIGN_MARKER_WORDING[r.campaignMarker].label}
      </span>
    ),
  },
  { key: 'kind', label: 'Kind', sortValue: (r) => CAME_BACK_WORDING[r.kind], render: (r) => <span className="pill pill-success">{CAME_BACK_WORDING[r.kind]}</span> },
  {
    key: 'stopped', label: 'Said STOP', firstDir: 'desc', sortValue: (r) => (r.stoppedAt ? Date.parse(r.stoppedAt) || null : null),
    render: (r) => (
      <>
        {r.stoppedAt ? fmtTs(r.stoppedAt) : <span className="muted">never</span>}
        {r.stopChannel ? <div className="micro muted">{r.stopChannel}</div> : null}
      </>
    ),
  },
  {
    key: 'restarted', label: 'Said START', firstDir: 'desc', sortValue: (r) => (r.restartedAt ? Date.parse(r.restartedAt) || null : null),
    render: (r) => (
      <>
        {r.restartedAt ? fmtTs(r.restartedAt) : <span className="muted">-</span>}
        {r.restartChannel ? <div className="micro muted">{r.restartChannel}</div> : null}
      </>
    ),
  },
  { key: 'gap', label: 'Gap', sortValue: (r) => r.gapSeconds, render: (r) => fmtGap(r.gapSeconds) || <span className="muted">-</span> },
  { key: 'source', label: 'Source', tdClassName: 'micro', sortValue: (r) => r.source, render: (r) => r.source },
];

export function OptOutReview(): JSX.Element {
  const [mode, setMode] = useState<Mode>('all');
  /** '' is every row; a marker narrows to one of the three states. His side table is the not-on-current filter. */
  const [marker, setMarker] = useState<'' | CampaignMarker>('');
  /** '' is every row; a cohort (his 226) narrows to that extract's rows that name a member. */
  const [cohort, setCohort] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<OptOutReviewResp | null>(null);
  const [rev, setRev] = useState<OptOutReversalsResp | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [directNote, setDirectNote] = useState('');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  /**
   * Row 216: a platform call whose outcome came back UNKNOWN is retried on the SAME id, so the
   * platform replays its first answer instead of applying twice. Any other outcome spends the id.
   */
  const [retryIds, setRetryIds] = useState<Record<string, string>>({});

  const classes = useMemo(
    () => (mode === 'deciding' ? DECIDING.join(',') : mode === 'confirming' ? CONFIRMING.join(',') : ''),
    [mode],
  );

  /**
   * ROW 356: the shared table. The review's sort (up to three keys) and slicers go to the SERVER,
   * because this screen holds one page; Came back is one whole list and sorts and slices here. The
   * review's columns are built below, where the decision cell can reach its handlers.
   */
  const view = useGridView('optout-review', reviewColumns(), {
    defaultSort: [{ k: 'at', asc: true }],
    slicers: REVIEW_SLICER_DEFS,
    serverSortable: SORT_KEYS,
  });
  const cameBack = useGridView('optout-came-back', CAME_BACK_COLUMNS, { defaultSort: [], slicers: CAME_BACK_SLICERS });
  const sortWire = sortParam(view.sort);
  const sliceWire = slicerParam(view.slicers);
  useEffect(() => { setOffset(0); }, [sortWire, sliceWire]);

  // Row 359 B: a failed RE-read of the view on screen keeps its rows, stale and locked (lib/stale-read).
  const viewKey = JSON.stringify([mode, classes, q, offset, sortWire, sliceWire, marker, cohort]);
  const { staleAt, ok: readOk, failed: readFailed } = useStaleRead();

  const load = useCallback(async () => {
    setErr('');
    if (mode === 'reversals') {
      // ITS OWN LIST, its own route. Never fetched through the review list, so a came-back member can
      // never be rendered beside a stop or handed a decision button.
      try {
        setRev(await optOutReversals(q));
        readOk(viewKey);
      } catch (e) {
        if (!readFailed(viewKey)) setRev(null);
        setErr(e instanceof Error ? e.message : String(e));
      }
      return;
    }
    try {
      // SORTED SERVER-SIDE. Sorting the delivered page in the browser would reorder only the loaded
      // 40 of 1,294, so "oldest unreviewed" would silently mean "oldest among this page".
      const res = await optOutReview({
        filter: mode === 'all' ? 'all' : mode === 'decided' ? 'decided' : 'pending',
        class: classes,
        q,
        sort: sortWire,
        slice: sliceWire,
        limit: PAGE,
        cohort,
        withMember: cohort ? '1' : '',
        offset,
        marker,
      });
      setData(res);
      readOk(viewKey);
    } catch (e) {
      // A failed read of a NEW view shows an error and NO rows. Quietly showing a stale or empty list on
      // a screen about who may be contacted is the one failure worth being loud about; a failed RE-read
      // keeps the rows under a red line with every action off, which is just as loud (row 359 B).
      if (!readFailed(viewKey)) setData(null);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [mode, classes, q, offset, sortWire, sliceWire, marker, cohort, viewKey, readOk, readFailed]);

  useEffect(() => { void load(); }, [load]);

  async function decide(row: OptOutReviewRow, decision: 'approved' | 'rejected' | 'unclear'): Promise<void> {
    const reason = (reasons[row.rowId] ?? '').trim();
    if (decision === 'rejected' && !reason) {
      setErr('A rejection needs a reason: it becomes an un-block request sent to the texting platform.');
      return;
    }
    if (decision === 'rejected' && data?.platformWrite && reason.length < MIN_REASON) {
      setErr(`A rejection needs a reason of at least ${MIN_REASON} characters: the texting platform keeps it as the record of why this member is reachable again.`);
      return;
    }
    setBusy(row.rowId);
    setErr('');
    try {
      const res = await optOutDecide(row.rowId, decision, reason, retryIds[row.rowId]);
      const p = res.directWrite?.platform;
      if (p?.attempted && p.status === 'unknown' && p.clientRequestId) {
        // Keep the reason too, so the retry is the same request.
        setRetryIds((m) => ({ ...m, [row.rowId]: p.clientRequestId as string }));
      } else {
        setRetryIds((m) => { const n = { ...m }; delete n[row.rowId]; return n; });
        setReasons((r) => ({ ...r, [row.rowId]: '' }));
      }
      // Row 216: say what happened to the record and the platform, in the API's own words. A record
      // that was NOT written is an error on this screen, because the decision stands here and not there.
      const sentence = directWriteSentence(res.directWrite);
      if (sentence.startsWith('Recorded here, but')) setErr(sentence);
      else setDirectNote(sentence);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  }

  /**
   * The review's columns (row 356). Each cell is the cell the review has always shown; the class and
   * the words are never sortable on the server, which has no meaningful order for them.
   */
  function reviewColumns(): GridColumn<OptOutReviewRow>[] {
    return [
      {
        key: 'member', label: 'Member',
        render: (r) => (
          <>
            <div>{r.memberName || <span className="muted">unmatched</span>}</div>
            <div className="micro muted">
              {r.memberId ? fmtMemberId(r.memberId) : ''}{r.phone ? ` - ${fmtPhone(r.phone)}` : ''}
            </div>
          </>
        ),
      },
      {
        key: 'campaign', label: 'Campaign',
        render: (r) => (
          <span className={`pill ${CAMPAIGN_MARKER_WORDING[r.campaignMarker].pill}`} title={CAMPAIGN_MARKER_WORDING[r.campaignMarker].help}>
            {CAMPAIGN_MARKER_WORDING[r.campaignMarker].label}
          </span>
        ),
      },
      {
        key: 'class', label: 'Class',
        render: (r) => (
          <span className={`pill ${CLASS_PILL[r.class] ?? 'pill-accent'}`} title={CLASS_HELP[r.class] ?? ''}>
            {r.class}
          </span>
        ),
      },
      {
        key: 'said', label: 'What the member said', tdStyle: { maxWidth: 420 },
        render: (r) =>
          r.communicationIsMemberWords ? (
            <span>{r.communication}</span>
          ) : (
            // NEVER render a bare do-not-solicit timestamp as though it were speech. An admin
            // approving a permanent suppression must not be approving a date.
            <span className="muted" style={{ fontStyle: 'italic' }}>{r.communicationNote}</span>
          ),
      },
      { key: 'channel', label: 'Channel', render: (r) => r.channel || <span className="muted">-</span> },
      { key: 'at', label: 'When', render: (r) => (r.at ? fmtTs(r.at) : <span className="muted">not recorded</span>) },
      {
        key: 'source', label: 'Source', tdClassName: 'micro',
        render: (r) => (
          <>
            {r.source}
            {r.basisSource && <div className="muted">{COHORT_WORDING[r.basisSource]?.label ?? r.basisSource}</div>}
          </>
        ),
      },
      { key: 'decision', label: 'Decision', locked: true, tdStyle: { minWidth: 300 }, render: (r) => decisionCell(r) },
    ];
  }

  /** The Decision cell: the decision already recorded, or the reason box and the three buttons. */
  function decisionCell(r: OptOutReviewRow): JSX.Element {
    return (
      <>
      {r.decision && r.decision.decision !== 'unclear' ? (
        <div className="micro">
          <strong>{r.decision.decision}</strong> by {r.decision.byName}
          <div className="muted">{fmtTs(r.decision.at)}</div>
          {r.decision.reason && <div className="muted">{r.decision.reason}</div>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {r.decision && (
            // An 'unclear' is a deferral: the row stays open and still takes a decision,
            // and the prior look is shown so the next reader does not repeat it blind.
            <div className="micro muted">
              marked unclear by {r.decision.byName}, {fmtTs(r.decision.at)}
              {r.decision.reason ? `: ${r.decision.reason}` : ''}
            </div>
          )}
          <input
            className="text-input"
            aria-label={`Reason for ${r.memberName || r.rowId}`}
            placeholder={data?.platformWrite ? `Reason (at least ${MIN_REASON} characters to reject)` : 'Reason (required to reject; optional otherwise)'}
            value={reasons[r.rowId] ?? ''}
            onChange={(e) => setReasons((s) => ({ ...s, [r.rowId]: e.target.value }))}
          />
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy === r.rowId}
              onClick={() => { void decide(r, 'approved'); }}
            >
              Approve the stop
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={busy === r.rowId || (reasons[r.rowId] ?? '').trim().length < (data?.platformWrite ? MIN_REASON : 1)}
              title={
                r.phone
                  ? 'Sends an un-block request to the texting platform with your reason.'
                  : 'This row has no phone number, so no un-block request can be made for it.'
              }
              onClick={() => { void decide(r, 'rejected'); }}
            >
              Not a stop
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={busy === r.rowId}
              title="A deferral: records that you looked and could not tell. Changes nothing for the member; the row stays here."
              onClick={() => { void decide(r, 'unclear'); }}
            >
              Can&rsquo;t tell
            </button>
          </div>
        </div>
      )}
      </>
    );
  }

  const counts = data?.counts ?? {};
  const decidingTotal = DECIDING.reduce((a, c) => a + (counts[c] ?? 0), 0);
  const confirmingTotal = CONFIRMING.reduce((a, c) => a + (counts[c] ?? 0), 0);
  const allTotal = Object.values(counts).reduce((a, n) => a + n, 0);
  const markers = data?.markers;
  // Only cohorts that name at least one member get a chip; the order is about members.
  const cohortEntries = Object.entries(data?.cohorts ?? {}).filter(([, c]) => c.withMember > 0).sort(([a], [b]) => a.localeCompare(b));
  const MARKER_ORDER: CampaignMarker[] = ['on-current', 'not-on-current', 'unknown'];

  return (
    <div className="page-pad">
      <div className="section-eyebrow">Member Care &middot; Opt-outs</div>
      <h1 className="section-title">Opt-outs</h1>
      <div className="section-rule" />
      <p className="section-lede">
        Every communication that suppresses a member, one row each, for review.
      </p>

      {/* The sentence the whole screen turns on. Not a tooltip. */}
      <div className="note" style={{ marginBottom: 14 }}>
        {OPTOUT_REVIEW_WORDING.approvalNote}
      </div>
      {data && data.platformWrite === false && (
        // His 226: the override reaches texting only when 216 goes live. Said whenever it is not live,
        // not only on the cohort view, because the approval note above promises an un-block request.
        <div className="note warn" style={{ marginBottom: 14 }}>
          {OPTOUT_REVIEW_WORDING.overrideQueuedNote}
        </div>
      )}

      {data?.emptyReason === 'capability-absent' && (
        <div className="note warn" style={{ marginBottom: 14 }}>
          The permanent opt-out record is not switched on for this deployment, so there is nothing to
          review here yet. This is a statement about the deployment, not about the members.
        </div>
      )}
      {data?.emptyReason === 'source-unreachable' && (
        <div className="note danger" style={{ marginBottom: 14 }}>
          The opt-out record could not be read, so no rows are shown. Nothing has changed for any
          member; the suppression they already have stays in force.
          {data.record.lastError ? ` (${data.record.lastError})` : ''}
        </div>
      )}
      {staleAt ? <StaleNote at={staleAt} err={err} onRetry={() => void load()} />
        : err && <div className="note danger" style={{ marginBottom: 14 }}>{err}</div>}
      {directNote && <div className="note" style={{ marginBottom: 14 }}>{directNote}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <button
          type="button"
          className={mode === 'all' ? 'chip selected' : 'chip'}
          onClick={() => { setMode('all'); setOffset(0); }}
        >
          All{allTotal ? ` (${allTotal})` : ''}
        </button>
        <button
          type="button"
          className={mode === 'deciding' ? 'chip selected' : 'chip'}
          onClick={() => { setMode('deciding'); setOffset(0); }}
        >
          Needs a decision{decidingTotal ? ` (${decidingTotal})` : ''}
        </button>
        <button
          type="button"
          className={mode === 'confirming' ? 'chip selected' : 'chip'}
          onClick={() => { setMode('confirming'); setOffset(0); }}
        >
          Stops to confirm{confirmingTotal ? ` (${confirmingTotal})` : ''}
        </button>
        <button
          type="button"
          className={mode === 'decided' ? 'chip selected' : 'chip'}
          onClick={() => { setMode('decided'); setOffset(0); }}
        >
          Already reviewed
        </button>
        <button
          type="button"
          className={mode === 'reversals' ? 'chip selected' : 'chip'}
          onClick={() => { setMode('reversals'); setOffset(0); }}
          title="Members who said STOP and then came back. Off the record; nothing to approve."
        >
          Came back{rev && mode === 'reversals' ? ` (${rev.total})` : ''}
        </button>
        <input
          className="text-input"
          aria-label="Search the review list"
          placeholder="Name, member id, phone, or words"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOffset(0); }}
          style={{ minWidth: 240 }}
        />
      </div>

      {mode !== 'reversals' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
          <span className="micro muted">Campaign{data?.campaign ? ` (${data.campaign})` : ''}:</span>
          <button type="button" className={marker === '' ? 'chip selected' : 'chip'} onClick={() => { setMarker(''); setOffset(0); }}>
            Everyone
          </button>
          {MARKER_ORDER.map((m) => (
            <button
              key={m}
              type="button"
              className={marker === m ? 'chip selected' : 'chip'}
              title={CAMPAIGN_MARKER_WORDING[m].help}
              onClick={() => { setMarker(m); setOffset(0); }}
            >
              {CAMPAIGN_MARKER_WORDING[m].label}{markers ? ` (${markers[m]})` : ''}
            </button>
          ))}
          {data && data.rosterAvailable === false && (
            <span className="micro muted">roster could not be read, so every row is &ldquo;cannot tell&rdquo;</span>
          )}
        </div>
      )}
      {mode !== 'reversals' && cohortEntries.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
          <span className="micro muted">Found in:</span>
          <button type="button" className={cohort === '' ? 'chip selected' : 'chip'} onClick={() => { setCohort(''); setOffset(0); }}>
            Every source
          </button>
          {cohortEntries.map(([k, c]) => (
            <button
              key={k}
              type="button"
              className={cohort === k ? 'chip selected' : 'chip'}
              title={COHORT_WORDING[k]?.help ?? `Rows whose published basis names source ${k}.`}
              onClick={() => { setCohort(k); setOffset(0); }}
            >
              {COHORT_WORDING[k]?.label ?? k} ({c.withMember} member{c.withMember === 1 ? '' : 's'}, {c.withMemberPending} to review)
            </button>
          ))}
        </div>
      )}
      {mode !== 'reversals' && cohort && COHORT_WORDING[cohort] && (
        <div className="note" style={{ marginBottom: 10 }}>{COHORT_WORDING[cohort].help}</div>
      )}
      {mode !== 'reversals' && (marker === 'not-on-current' || (markers && markers['not-on-current'] > 0)) && (
        // THE ENFORCEMENT SENTENCE, both halves, beside the filter that invites the wrong inference.
        <div className="note" style={{ marginBottom: 10 }}>{OPTOUT_REVIEW_WORDING.markerNote}</div>
      )}

      {mode === 'deciding' && (
        <div className="micro muted" style={{ marginBottom: 8 }}>
          These are the rows short of a definitive stop, where your reading changes the outcome. The
          already-definitive stops are under &ldquo;Stops to confirm&rdquo;; every one of them is here to be
          reviewed too, they are simply not where the work is.
        </div>
      )}

      {mode === 'reversals' && (
        <>
          <div className="note" style={{ marginBottom: 10 }}>{OPTOUT_REVIEW_WORDING.reversalsNote}</div>
          {rev?.emptyReason === 'source-unreachable' && (
            <div className="note danger" style={{ marginBottom: 10 }}>
              The came-back file could not be read, so no rows are shown.
              {rev.reversals.lastError ? ` (${rev.reversals.lastError})` : ''}
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <CareGrid
              view={cameBack}
              rows={rev?.rows ?? []}
              rowKey={(r) => `${r.memberId}-${r.restartedAt}`}
              noun="members"
              empty={err ? '' : rev?.emptyReason && rev.emptyReason !== 'no-rows' ? '' : 'Nobody has come back yet.'}
            />
          </div>
          <div className="micro muted" style={{ marginTop: 10 }}>
            {rev?.reversals.publishedAt ? `came-back file published ${fmtTs(rev.reversals.publishedAt)}` : ''}
            {rev?.reversals.campaign ? ` - campaign ${rev.reversals.campaign}` : ''}
          </div>
        </>
      )}

      <fieldset disabled={!!staleAt} style={LOCK_FIELDSET_STYLE}>
      <div style={{ overflowX: 'auto', display: mode === 'reversals' ? 'none' : undefined }}>
        <CareGrid
          view={view}
          rows={data?.rows ?? []}
          rowKey={(r) => r.rowId}
          noun="rows"
          server={{ facets: data?.facets ?? {}, hidden: data?.sliceHidden ?? 0 }}
          empty={err ? '' : data?.emptyReason ? '' : 'Nothing here to review.'}
        />
      </div>
      </fieldset>

      <div style={{ display: mode === 'reversals' ? 'none' : 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
        <div className="micro muted">
          {data ? `${data.total} row${data.total === 1 ? '' : 's'}, ${data.pending} still to review` : ''}
          {data?.record.publishedAt ? ` - record published ${fmtTs(data.record.publishedAt)}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" className="btn btn-sm btn-ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Previous
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={!data || offset + PAGE >= data.total}
            onClick={() => setOffset(offset + PAGE)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
